/**
 * useSubmissionPayload - Build API Submission Payload
 *
 * Single responsibility: Transforming selection state into API payload format.
 * Pure transformation logic with no side effects.
 */

import { useCallback } from 'react';
import type { PickemDetailResponse } from '@low6-pickem/api-types';
import { QUESTION_TYPES } from '@/constants/question-types';
import { getQuestionId } from '@/utils/id-helpers';
import { findOptionByAnswer } from '@/utils/pickem-helpers';
import type { SelectionState } from './useSelectionState';
import { findOverUnderOption } from '@/utils/over-under-option';

/**
 * Submission payload item shape
 */
export interface SubmissionPayload {
  question_id: string;
  option_id?: string; // Optional for tiebreaker questions
  custom_value?: string;
}

/**
 * Creates function to build submission payload from selections
 *
 * @param pickemDetails - Pickem details containing questions
 * @param selections - Current selection state
 * @returns Object with buildSubmissionPayload function
 */
export function useSubmissionPayload(
  pickemDetails: PickemDetailResponse | undefined,
  selections: SelectionState
) {
  /**
   * Build submission payload for API
   *
   * @returns Array of submission payload items
   */
  const buildSubmissionPayload = useCallback((): SubmissionPayload[] => {
    if (!pickemDetails?.pickem.questions) return [];

    const payload: SubmissionPayload[] = [];

    // Add team selections
    for (const [questionId, optionId] of Object.entries(selections.teams)) {
      payload.push({
        question_id: questionId,
        option_id: optionId,
      });
    }

    // Add prop selections
    for (const [questionId, answer] of Object.entries(selections.props)) {
      const question = pickemDetails.pickem.questions.find(q => getQuestionId(q) === questionId);

      if (question) {
        const optionId = findOptionByAnswer(question, answer);
        if (optionId) {
          payload.push({
            question_id: questionId,
            option_id: optionId,
          });
        }
      }
    }

    // Add over/under selections
    for (const [questionId, answer] of Object.entries(selections.overUnder)) {
      const question = pickemDetails.pickem.questions.find(q => getQuestionId(q) === questionId);

      if (question) {
        const selectedOption = findOverUnderOption(question, answer);

        if (selectedOption) {
          const optionId = selectedOption.option_id || selectedOption.id;
          if (optionId) {
            payload.push({
              question_id: questionId,
              option_id: String(optionId),
            });
          }
        }
      }
    }

    // Add tiebreaker
    if (selections.tieBreaker) {
      const tiebreakerQuestion = pickemDetails.pickem.questions.find(
        q => q.question_type === QUESTION_TYPES.TIEBREAKER
      );
      if (tiebreakerQuestion) {
        // Tiebreaker questions don't have option_id, only custom_value
        payload.push({
          question_id: getQuestionId(tiebreakerQuestion),
          custom_value: selections.tieBreaker,
        });
      }
    }

    return payload;
  }, [pickemDetails, selections]);

  return {
    buildSubmissionPayload,
  };
}
